import { FlagshipSlider } from '@wwe-portal/ui/features/offer';
import { FC, SyntheticEvent, useEffect, useMemo, useRef } from 'react';
import { useCurrentCurrency } from '@wwe-portal/web/hooks/global/use-current-currency';
import { OfferData } from '@wwe-portal/entities/offer.type';
import { useViewedOfferIds } from '@wwe-portal/web/providers/stores/use-pip-store';
import { offerToProps } from '@wwe-portal/web/utils/offer/mapper';
import { CarouselApi, useCarousel } from '@wwe-portal/ui/components/carousel';
import { sendFlagshipOfferSlideEvent } from '@wwe-portal/web/libs/analytics/offer';
import { useIsAuthenticated } from '@wwe-portal/web/providers/stores/auth-store';
import { useGetUserStocks } from '@wwe-portal/web/providers/queries/offer';

interface FlagshipAdapterProps {
  offers: OfferData[] | undefined;
  onClick: (offerId: string, e: SyntheticEvent) => void;
  onClickPrice: (
    offerId: string,
    e: SyntheticEvent,
    analyticsContext: {
      slideIndex: number;
      totalSlides: number;
    }
  ) => void;
}

const FlagshipSlideAnalaytics: FC<{
  flagshipOffers: OfferData[];
}> = ({ flagshipOffers }) => {
  const previousIndexRef = useRef<number>(0);
  const { api } = useCarousel();

  useEffect(() => {
    const cb = (currentApi: CarouselApi) => {
      if (currentApi == null) return;
      const index = currentApi.selectedScrollSnap();
      const previousIndex = previousIndexRef.current;
      previousIndexRef.current = index;
      if (previousIndex == null) return;

      const nodeCount = currentApi.slideNodes().length;
      const currentOffer = flagshipOffers[index];
      const previousOffer = flagshipOffers[previousIndex];

      sendFlagshipOfferSlideEvent({
        previousOfferId: previousOffer?.OffersID,
        currentOfferId: currentOffer?.OffersID,
        currentIndex: index,
        previousIndex,
        totalSlots: nodeCount,
      });
    };
    api?.on('slidesInView', cb);
    return () => {
      api?.off('slidesInView', cb);
    };
  }, [api]);

  return null;
};

export const FlagshipAdapter: FC<FlagshipAdapterProps> = ({
  offers,
  onClick,
  onClickPrice,
}) => {
  const { currencies } = useCurrentCurrency();
  const { stockMap } = useGetUserStocks();
  const offerViewedData = useViewedOfferIds();
  const isAuthenticated = useIsAuthenticated();
  const items = useMemo(() => {
    const offerList =
      offers?.map((offer) => ({
        ...offerToProps(offer, {
          stockMap,
          currencies,
          isAuthenticated,
        }),
        description: offer.StoreItemDescription,
        hasPip: !offerViewedData.has(offer.OffersID),
      })) ?? [];

    return offerList.filter((offer) => (offer?.quantity ?? 0) > 0);
  }, [offers, currencies, stockMap, offerViewedData, isAuthenticated]);

  return (
    <FlagshipSlider items={items} onClick={onClick} onClickPrice={onClickPrice}>
      {offers && <FlagshipSlideAnalaytics flagshipOffers={offers} />}
    </FlagshipSlider>
  );
};
